import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { offerPeriodKey, useProductHuntOffer } from '@/hooks/useProductHuntOffer'

const DISMISS_KEY = 'planwise_ph_offer_dismissed'

function readDismissed(): string | null {
    try { return localStorage.getItem(DISMISS_KEY) } catch { return null }
}

/**
 * Strip above the public header telling Product Hunt visitors about their
 * first-payment discount. Shown only when the visit is attributed to Product
 * Hunt (guests) or the server says this account can still use the offer
 * (logged in), and only while a promotion is actually running, so it can never
 * advertise a discount checkout would refuse.
 *
 * It sits inside the fixed header and reports its height in
 * `--promo-banner-h`; the body is padded by the same amount so page content,
 * which is offset for the header alone, does not slide under it.
 */
export function ProductHuntBanner() {
    const { t, i18n } = useTranslation()
    const found = useProductHuntOffer()
    const offer = found && { ...found, href: found.deadline ? `/offers/${found.id}` : '/register' }
    const [dismissed, setDismissed] = useState(readDismissed)
    const ref = useRef<HTMLDivElement>(null)

    const visible = Boolean(offer && dismissed !== offer.id)

    useEffect(() => {
        const el = ref.current
        if (!visible || !el) return
        const root = document.documentElement
        const apply = () => {
            const height = `${el.offsetHeight}px`
            root.style.setProperty('--promo-banner-h', height)
            document.body.style.paddingTop = height
        }
        apply()
        const observer = new ResizeObserver(apply)
        observer.observe(el)
        return () => {
            observer.disconnect()
            root.style.removeProperty('--promo-banner-h')
            document.body.style.paddingTop = ''
        }
    }, [visible])

    if (!offer || !visible) return null

    const period = t(offerPeriodKey(offer.intervals))
    const deadline = offer.deadline
        ? new Date(offer.deadline).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', timeZone: 'Asia/Jakarta' })
        : ''
    const chip = <strong className="font-bold" />
    const values = { percentage: offer.percentage, period, date: deadline, days: offer.trialDays }
    const cta = offer.deadline ? t('promoBanner.accountCta') : t('promoBanner.guestCta')
    const dismiss = () => {
        setDismissed(offer.id)
        try { localStorage.setItem(DISMISS_KEY, offer.id) } catch { /* Hidden for this visit only. */ }
        // The focused button disappears; keep keyboard users in the header instead of at the top of the document.
        document.querySelector<HTMLElement>('header nav a')?.focus({ preventScroll: true })
    }

    return (
        <div
            ref={ref}
            role="region"
            aria-label={t('promoBanner.label')}
            data-promo-hide
            className="pointer-events-auto relative w-full bg-gradient-to-r from-primary-600 via-purple-600 to-pink-600 text-white dark:from-primary-700 dark:via-purple-700 dark:to-pink-700"
        >
            <div className="mx-auto flex min-h-11 sm:min-h-10 max-w-[1626px] items-center justify-center gap-x-2 sm:gap-x-3 py-1.5 pl-4 pr-12 text-[13px] sm:text-sm">
                <span translate="no" className="hidden shrink-0 rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ring-white/25 sm:inline">
                    {t('promoBanner.tag')}
                </span>
                <p className="min-w-0 truncate font-medium sm:whitespace-normal">
                    {/* Phones get the short line so the strip stays one row tall. */}
                    <span className="sm:hidden">
                        <Trans i18nKey="promoBanner.short" values={values} components={{ chip }} />
                    </span>
                    <span className="hidden sm:inline">
                        <Trans i18nKey={offer.deadline ? 'promoBanner.account' : 'promoBanner.guest'} values={values} components={{ chip }} />
                    </span>
                </p>
                <Link
                    to={offer.href}
                    className="group inline-flex shrink-0 items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-primary-700 shadow-sm transition-colors hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-purple-600"
                >
                    {cta}
                    <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
                </Link>
            </div>
            <button
                type="button"
                onClick={dismiss}
                aria-label={t('promoBanner.dismiss')}
                className="absolute right-0.5 top-1/2 flex h-11 w-11 sm:h-10 sm:w-10 -translate-y-1/2 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
        </div>
    )
}

export default ProductHuntBanner
